import * as i0 from '@angular/core';
import { HostListener, Input, Directive, EventEmitter, Output, NgModule, SecurityContext, Pipe, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { isArrayNotEmpty } from '@engineering11/utility';
import * as i1 from '@angular/platform-browser';
import { DomSanitizer } from '@angular/platform-browser';
import { NgxSummernoteViewDirective } from 'ngx-summernote';

class E11TitleDirective {
    constructor(el) {
        this.el = el;
        this.e11title = ''; // The text for the title to display
        this.e11titlePosition = 'top'; // right, bottom, no left is intentional
        this.delay = 250; // Give the user 1/4 of a second before title pops up
        this.parentTop = 0;
        this.parentLeft = 0;
        this.parentWidth = 0;
        this.parentHeight = 0;
    }
    ngOnDestroy() {
        if (this.myPopup) {
            this.myPopup.remove();
        }
    }
    onMouseEnter() {
        // Get the 'parents' position so we can set position of the title popup.
        this.parentTop = window.pageYOffset + this.el.nativeElement.getBoundingClientRect().top;
        this.parentLeft = window.pageXOffset + this.el.nativeElement.getBoundingClientRect().left;
        this.parentWidth = this.el.nativeElement.offsetWidth;
        this.parentHeight = this.el.nativeElement.offsetHeight;
        // Get the parents size.
        this.timer = setTimeout(() => {
            this.e11title ? this.createTooltipPopup() : null;
        }, this.delay);
    }
    onMouseLeave() {
        if (this.timer)
            clearTimeout(this.timer);
        if (this.myPopup) {
            this.myPopup.remove();
        }
    }
    createTooltipPopup() {
        let id = `e11title-${Math.ceil(Math.random() * 1000000).toString()}`;
        let popup = document.createElement('div');
        popup.innerHTML = this.e11title;
        popup.setAttribute('class', 'e11-title-container');
        popup.setAttribute('id', id);
        // Make popup and get it in dom to get size and position it properly
        this.el.nativeElement.appendChild(popup);
        this.myPopup = popup;
        let element = document?.getElementById(id);
        if (this.e11titlePosition === 'top') {
            popup.classList.add('top');
            popup.style.top = this.parentTop - element.offsetHeight - 8 + 'px';
            popup.style.left = this.parentLeft - (element.offsetWidth / 2 - this.parentWidth / 2) + 'px';
        }
        else if (this.e11titlePosition === 'bottom') {
            popup.classList.add('bottom');
            popup.style.top = this.parentTop + this.parentHeight + 8 + 'px';
            popup.style.left = this.parentLeft - (element.offsetWidth / 2 - this.parentWidth / 2) + 'px';
        }
        else if (this.e11titlePosition === 'right') {
            popup.classList.add('right');
            popup.style.top = this.parentTop + (this.parentHeight - element.offsetHeight) / 2 + 'px';
            popup.style.left = this.parentLeft + this.parentWidth + 8 + 'px';
        }
        // Turn the title off after a few seconds? I dunno. Let it stay on as long as the user is hovering?
        // setTimeout(() => {
        //   if (this.myPopup) this.myPopup.remove();
        // }, 5000);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11TitleDirective, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.13", type: E11TitleDirective, isStandalone: true, selector: "[e11title]", inputs: { e11title: "e11title", e11titlePosition: "e11titlePosition", delay: "delay" }, host: { listeners: { "mouseenter": "onMouseEnter()", "mouseleave": "onMouseLeave()" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11TitleDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[e11title]',
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }], propDecorators: { e11title: [{
                type: Input
            }], e11titlePosition: [{
                type: Input
            }], delay: [{
                type: Input
            }], onMouseEnter: [{
                type: HostListener,
                args: ['mouseenter']
            }], onMouseLeave: [{
                type: HostListener,
                args: ['mouseleave']
            }] } });

class ClickOutsideDirective {
    constructor(elementRef) {
        this.elementRef = elementRef;
        this.closeOnClickOutside = new EventEmitter();
    }
    onClick(target) {
        const clickedInside = this.elementRef.nativeElement.contains(target);
        if (!clickedInside) {
            this.closeOnClickOutside.emit();
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ClickOutsideDirective, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.13", type: ClickOutsideDirective, isStandalone: true, selector: "[closeOnClickOutside]", outputs: { closeOnClickOutside: "closeOnClickOutside" }, host: { listeners: { "document:click": "onClick($event.target)" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ClickOutsideDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[closeOnClickOutside]',
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }], propDecorators: { closeOnClickOutside: [{
                type: Output
            }], onClick: [{
                type: HostListener,
                args: ['document:click', ['$event.target']]
            }] } });

class EscapeKeyDirective {
    constructor() {
        this.closeOnEscape = new EventEmitter();
    }
    onKeydownHandler(event) {
        this.closeOnEscape.emit();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: EscapeKeyDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.13", type: EscapeKeyDirective, isStandalone: true, selector: "[closeOnEscape]", outputs: { closeOnEscape: "closeOnEscape" }, host: { listeners: { "document:keydown.escape": "onKeydownHandler($event.target)" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: EscapeKeyDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[closeOnEscape]',
                    standalone: true,
                }]
        }], propDecorators: { closeOnEscape: [{
                type: Output
            }], onKeydownHandler: [{
                type: HostListener,
                args: ['document:keydown.escape', ['$event.target']]
            }] } });

class E11StopClickDirective {
    onClick(event) {
        if (this.apply) {
            return;
        }
        event.stopPropagation();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11StopClickDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.13", type: E11StopClickDirective, isStandalone: true, selector: "[e11-stop-click]", inputs: { apply: "apply" }, host: { listeners: { "click": "onClick($event)" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11StopClickDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[e11-stop-click]',
                    standalone: true,
                }]
        }], propDecorators: { apply: [{
                type: Input
            }], onClick: [{
                type: HostListener,
                args: ['click', ['$event']]
            }] } });

class E11AppendToDirective {
    constructor(el, renderer) {
        this.el = el;
        this.renderer = renderer;
    }
    ngAfterViewInit() {
        const hostElement = this.el.nativeElement;
        if (this.target === 'body') {
            this.targetElement = document.body;
        }
        else {
            this.targetElement = document.getElementById(this.target);
        }
        if (this.targetElement) {
            this.renderer.appendChild(this.targetElement, hostElement);
            return;
        }
        console.warn(`Invalid appendTo target: ${this.target}`);
    }
    ngOnDestroy() {
        if (this.targetElement && this.el.nativeElement) {
            // Remove the element when the directive is destroyed
            this.renderer.removeChild(this.targetElement, this.el.nativeElement);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11AppendToDirective, deps: [{ token: i0.ElementRef }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.13", type: E11AppendToDirective, isStandalone: true, selector: "[e11AppendTo]", inputs: { target: ["e11AppendTo", "target"] }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11AppendToDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[e11AppendTo]',
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: i0.Renderer2 }], propDecorators: { target: [{
                type: Input,
                args: ['e11AppendTo']
            }] } });

var ScrollBarPosition;
(function (ScrollBarPosition) {
    ScrollBarPosition["Top"] = "top";
    ScrollBarPosition["Bottom"] = "bottom";
    ScrollBarPosition["Left"] = "left";
    ScrollBarPosition["Right"] = "right";
})(ScrollBarPosition || (ScrollBarPosition = {}));
var ScrollBarAxis;
(function (ScrollBarAxis) {
    ScrollBarAxis["X"] = "x";
    ScrollBarAxis["Y"] = "y";
})(ScrollBarAxis || (ScrollBarAxis = {}));
/**
 * Customizable scrollbar attribute directive
 *
 * You need two elements to use this directive:
 *
 * 1. A container element with the directive applied to it
 *    - This element will have styles applied like position: relative, padding: 40px, overflow: hidden, user-select: none
 *
 * 2. A child scrollable element with a class of 'e11-scrollable-content'
 *    - This class should be applied manually to the scrollable element
 *    - The directive get the element with this class and applies styles like width: 100%, height: 100%, overflow: scroll, scrollbar-width: none
 *
 * You can override the default styles by passing an input to the directive
 *
 * It is standalone directive, so you can use it without importing any module
 * Or you can import the WebUtilityModule and use it as a part of the module
 *
 * @example
 * <div [e11ScrollBar]="{top: true, right: true}" scrollBarClass="custom-scrollbar" scrollBarThumbClass="scroll-thumb">
 *  <div class="e11-scrollable-content">...</div>
 * </div>
 */
class E11ScrollBarDirective {
    /**
     * Input for positions of the scrollbars
     * @type {IPositionsArg}
     */
    set e11ScrollBar(value) {
        this.positions = Object.entries(value)
            .filter(([, value]) => value)
            .map(([key]) => key);
    }
    /**
     * Input for class names to be applied to the scrollbars
     * @type {string}
     */
    set scrollBarTrackClass(value) {
        this._scrollBarTrackClass = value?.split(' ') || [];
    }
    /**
     * Input for class names to be applied to the scroll thumb
     * @type {string}
     */
    set scrollBarThumbClass(value) {
        this._scrollBarThumbClass = value?.split(' ') || [];
    }
    constructor(el, renderer) {
        this.el = el;
        this.renderer = renderer;
        /**
         * The scrollbars created by the directive
         */
        this.scrollBars = [];
        /**
         * Event unlisteners to remove the event listeners when the directive is destroyed
         */
        this.eventUnlisteners = [];
        /**
         * The positions of the scrollbars
         * @type {ScrollBarPosition[]}
         */
        this.positions = [];
        /**
         * The class names to be applied to the scrollbars
         * @type {string[]}
         */
        this._scrollBarTrackClass = [];
        /**
         * The class names to be applied to the scroll thumb
         * @type {string[]}
         */
        this._scrollBarThumbClass = [];
        /**
         * Updates the position of the scroll thumb based on the scroll position of the content
         * Public method to update the thumb position when the content is scrolled programmatically
         */
        this.updateThumbPosition = () => {
            this.scrollBars.forEach((scrollBar) => {
                this.updateThumb(scrollBar);
            });
        };
        this.scrollContainer = this.el.nativeElement;
        this.addClasses(this.scrollContainer, ['e11-relative', 'e11-select-none', 'e11-p-4', 'e11-overflow-hidden']);
    }
    addClasses(el, classes) {
        classes.forEach((className) => {
            this.renderer.addClass(el, className);
        });
    }
    removeClasses(el, classes) {
        classes.forEach((className) => {
            this.renderer.removeClass(el, className);
        });
    }
    ngAfterViewInit() {
        this.initContent();
        this.initScrollbars();
        this.initializeScrollable();
        this.initializeDraggable();
        this.initializeTouchable();
        this.injectStyle();
        this.updateThumbPosition();
    }
    /**
     * Selects the scrollable content element and assign it to the `content` property
     */
    initContent() {
        this.content = this.scrollContainer.querySelector('.e11-scrollable-content');
        if (!this.content)
            return;
        this.addClasses(this.content, ['e11-w-full', 'e11-h-full', 'e11-overflow-scroll', 'e11-scrollbar-none', 'e11-no-scrollbar']);
    }
    /**
     * Creates the scrollbars based on the positions provided
     */
    initScrollbars() {
        this.scrollBars = this.createScrollBars(this.positions, this.scrollContainer);
    }
    /**
     * Initializes the scroll event listener on the scrollable content
     */
    initializeScrollable() {
        const unlisten = this.renderer.listen(this.getContentElement(), 'scroll', this.updateThumbPosition);
        this.eventUnlisteners = this.eventUnlisteners.concat(unlisten);
    }
    /**
     * Initializes the draggable event listener on the scroll thumb
     */
    initializeDraggable() {
        this.scrollBars.forEach((scrollBar) => {
            const unlisten = this.renderer.listen(scrollBar.scrollThumb.node, 'mousedown', (e) => this.onThumbMouseDown(e, scrollBar));
            this.eventUnlisteners = this.eventUnlisteners.concat(unlisten);
        });
    }
    /**
     * Initializes the touchable event listener on the scroll thumb
     */
    initializeTouchable() {
        this.scrollBars.forEach((scrollBar) => {
            const unlisten = this.renderer.listen(scrollBar.scrollThumb.node, 'touchstart', (e) => {
                this.onThumbTouchStart(e, scrollBar);
            });
            this.eventUnlisteners = this.eventUnlisteners.concat(unlisten);
        });
    }
    /**
     *
     * @param {MouseEvent} event
     * @param {IScrollBar} scrollBar
     * @returns
     */
    onThumbMouseDown(event, scrollBar) {
        this.activateScrollBar(scrollBar);
        event.preventDefault();
        const startPosition = scrollBar.axis === 'y' ? event.clientY : event.clientX;
        const startScrollPosition = scrollBar.axis === 'y' ? this.getContentElement().scrollTop : this.getContentElement().scrollLeft;
        const onMouseMove = (e) => this.handleMouseMove(e, scrollBar, startPosition, startScrollPosition);
        let unlistenMouseMove;
        let unlistenMouseUp;
        const onMouseUp = () => this.deactivateScrollBar(scrollBar, unlistenMouseMove, unlistenMouseUp);
        unlistenMouseMove = this.renderer.listen('document', 'mousemove', onMouseMove);
        unlistenMouseUp = this.renderer.listen('document', 'mouseup', onMouseUp);
    }
    onThumbTouchStart(event, scrollBar) {
        this.activateScrollBar(scrollBar);
        event.preventDefault();
        const startPosition = scrollBar.axis === 'y' ? event.touches[0].clientY : event.touches[0].clientX;
        const startScrollPosition = scrollBar.axis === 'y' ? this.getContentElement().scrollTop : this.getContentElement().scrollLeft;
        const onTouchMove = (e) => this.handleTouchMove(e, scrollBar, startPosition, startScrollPosition);
        let unlistenTouchMove;
        let unlistenTouchEnd;
        const onTouchEnd = () => this.deactivateScrollBar(scrollBar, unlistenTouchMove, unlistenTouchEnd);
        unlistenTouchMove = this.renderer.listen('document', 'touchmove', onTouchMove);
        unlistenTouchEnd = this.renderer.listen('document', 'touchend', onTouchEnd);
    }
    /**
     *  Handles the mouse move event
     */
    handleMouseMove(e, scrollBar, startPosition, startScrollPosition) {
        const currentPosition = scrollBar.axis === 'y' ? e.clientY : e.clientX;
        this.handleScrollBarMove(currentPosition, startPosition, startScrollPosition, scrollBar);
    }
    /**
     * Handles the touch move event
     */
    handleTouchMove(e, scrollBar, startPosition, startScrollPosition) {
        if (e.touches.length !== 1)
            return; // Ignore multi-touch
        e.preventDefault();
        const currentPosition = scrollBar.axis === 'y' ? e.touches[0].clientY : e.touches[0].clientX;
        this.handleScrollBarMove(currentPosition, startPosition, startScrollPosition, scrollBar);
    }
    /**
     * Handles the scroll bar move
     *
     */
    handleScrollBarMove(currentPosition, startPosition, startScrollPosition, scrollBar) {
        const delta = currentPosition - startPosition;
        const containerClientSize = this.scrollContainer[scrollBar.axis === 'y' ? 'clientHeight' : 'clientWidth'];
        const scrollThumbClientSize = scrollBar.scrollThumb.node[scrollBar.axis === 'y' ? 'clientHeight' : 'clientWidth'];
        const contentScrollSize = this.getContentElement()[scrollBar.axis === 'y' ? 'scrollHeight' : 'scrollWidth'];
        const scrollAmount = (delta / containerClientSize) * (contentScrollSize - scrollThumbClientSize);
        this.getContentElement()[scrollBar.axis === 'y' ? 'scrollTop' : 'scrollLeft'] = startScrollPosition + scrollAmount;
    }
    /**
     * Activates the scroll bar
     */
    activateScrollBar(scrollBar) {
        if (scrollBar.axis === 'y') {
            this.addClasses(scrollBar.node, ['e11-w-4']);
        }
        else {
            this.addClasses(scrollBar.node, ['e11-h-4']);
        }
    }
    /**
     * Handles scroll bar stop
     */
    deactivateScrollBar(scrollBar, ...unlistenFns) {
        if (scrollBar.axis === 'y') {
            this.removeClasses(scrollBar.node, ['e11-w-4']);
        }
        else {
            this.removeClasses(scrollBar.node, ['e11-h-4']);
        }
        unlistenFns.forEach((unlisten) => unlisten());
    }
    /**
     *
     * @param {ScrollBarPosition[]} positions
     * @param {HTMLElement} container
     * @returns
     */
    createScrollBars(positions, container) {
        return positions.map((position) => this.createScrollBar(position, container));
    }
    /**
     *
     * @param {ScrollBarPosition} position
     * @param {HTMLElement} container
     * @returns
     */
    createScrollBar(position, container) {
        const axis = position === ScrollBarPosition.Top || position === ScrollBarPosition.Bottom ? ScrollBarAxis.X : ScrollBarAxis.Y;
        const scrollbar = this.renderer.createElement('div');
        this.addClasses(scrollbar, ['e11-absolute', 'e11-rounded-md', 'e11-transition-all', 'e11-duration-500', 'e11-ease-in-out']);
        if (axis === 'y') {
            this.addClasses(scrollbar, ['e11-top-0', 'e11-w-1', 'e11-h-full', 'hover:e11-w-4', 'active:e11-w-4']);
        }
        else {
            this.addClasses(scrollbar, ['e11-left-0', 'e11-h-1', 'e11-w-full', 'hover:e11-h-4', 'active:e11-h-4']);
        }
        if (position === ScrollBarPosition.Top) {
            this.addClasses(scrollbar, ['e11-top-0']);
        }
        else if (position === ScrollBarPosition.Right) {
            this.addClasses(scrollbar, ['e11-right-0']);
        }
        else if (position === ScrollBarPosition.Bottom) {
            this.addClasses(scrollbar, ['e11-bottom-0']);
        }
        else if (position === ScrollBarPosition.Left) {
            this.addClasses(scrollbar, ['e11-left-0']);
        }
        this.renderer.appendChild(container, scrollbar);
        if (isArrayNotEmpty(this._scrollBarTrackClass)) {
            this.addClasses(scrollbar, this._scrollBarTrackClass);
        }
        const scrollThumb = this.renderer.createElement('div');
        this.addClasses(scrollThumb, [
            'e11-rounded-md',
            'e11-cursor-grab',
            'active:e11-cursor-grabbing',
            'e11-relative',
            'e11-select-none',
            'e11-transition-colors',
            'e11-duration-500',
        ]);
        if (axis === 'y') {
            this.addClasses(scrollThumb, ['e11-w-full', 'e11-h-4']);
        }
        else {
            this.addClasses(scrollThumb, ['e11-h-full', 'e11-w-4']);
        }
        this.renderer.appendChild(scrollbar, scrollThumb);
        if (isArrayNotEmpty(this._scrollBarThumbClass)) {
            this.addClasses(scrollThumb, this._scrollBarThumbClass);
        }
        return {
            axis,
            position,
            node: scrollbar,
            scrollThumb: { node: scrollThumb },
        };
    }
    /**
     *
     * @param {IScrollBar} scrollBar
     * @returns
     */
    updateThumb(scrollBar) {
        const contentScrollEdge = this.getContentElement()[scrollBar.axis == 'x' ? 'scrollLeft' : 'scrollTop'];
        const contentScrollSize = this.getContentElement()[scrollBar.axis == 'x' ? 'scrollWidth' : 'scrollHeight'];
        const contentClientSize = this.getContentElement()[scrollBar.axis == 'x' ? 'clientWidth' : 'clientHeight'];
        const scrollRatio = contentScrollEdge / (contentScrollSize - contentClientSize);
        const thumbSize = (contentClientSize / contentScrollSize) * contentClientSize;
        this.renderer.setStyle(scrollBar.scrollThumb.node, scrollBar.axis == 'x' ? 'width' : 'height', `${thumbSize}px`);
        this.renderer.setStyle(scrollBar.scrollThumb.node, scrollBar.axis == 'x' ? 'left' : 'top', `${scrollRatio * (scrollBar.node[scrollBar.axis == 'x' ? 'clientWidth' : 'clientHeight'] - thumbSize)}px`);
    }
    /**
     * Injects the styles for the custom scrollbar
     */
    injectStyle() {
        const style = this.renderer.createElement('style');
        style.innerHTML = /* css */ `
      /* Hide scrollbar for Chrome, Safari, and Opera */
      .e11-no-scrollbar::-webkit-scrollbar {
        display: none;
      }
      /* Hide scrollbar for IE, Edge, and Firefox */
      .e11-no-scrollbar {
        -ms-overflow-style: none;  /* IE and Edge */
        scrollbar-width: none;  /* Firefox */
      }`;
        this.renderer.appendChild(this.el.nativeElement, style);
    }
    /**
     * Removes the event listeners when the directive is destroyed
     */
    removeEventListeners() {
        this.eventUnlisteners.forEach((unlisten) => unlisten());
        console.debug('E11 custom scrollbar event listeners removed');
    }
    /**
     * Removes the scrollbars created by the directive
     */
    removeScrollBars() {
        this.scrollBars.forEach((scrollBar) => {
            this.renderer.removeChild(this.scrollContainer, scrollBar.node);
        });
        console.debug('E11 custom scrollbar removed');
    }
    /**
     * Removes the classes applied by the directive
     */
    cleanupClasses() {
        this.renderer.removeClass(this.scrollContainer, 'e11-scroll-container');
        console.debug('E11 custom scrollbar classes removed');
    }
    /**
     * Gets the scrollable content element
     */
    getContentElement() {
        if (!this.content) {
            console.error('No scrollable content found');
            this.cleanup();
            throw Error('No scrollable content found');
        }
        return this.content;
    }
    /**
     * Cleans up the directive
     */
    cleanup() {
        this.removeEventListeners();
        this.removeScrollBars();
        this.cleanupClasses();
        console.debug('E11 custom scrollbar directive destroyed');
    }
    ngOnDestroy() {
        this.cleanup();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11ScrollBarDirective, deps: [{ token: i0.ElementRef }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.13", type: E11ScrollBarDirective, isStandalone: true, selector: "[e11ScrollBar]", inputs: { e11ScrollBar: "e11ScrollBar", scrollBarTrackClass: "scrollBarTrackClass", scrollBarThumbClass: "scrollBarThumbClass" }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11ScrollBarDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[e11ScrollBar]',
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: i0.Renderer2 }], propDecorators: { e11ScrollBar: [{
                type: Input
            }], scrollBarTrackClass: [{
                type: Input
            }], scrollBarThumbClass: [{
                type: Input
            }] } });

const STANDALONE_DIRECTIVES = [ClickOutsideDirective, EscapeKeyDirective, E11TitleDirective, E11AppendToDirective, E11ScrollBarDirective];
class E11DirectivesModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11DirectivesModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: E11DirectivesModule, imports: [CommonModule, E11StopClickDirective, ClickOutsideDirective, EscapeKeyDirective, E11TitleDirective, E11AppendToDirective, E11ScrollBarDirective], exports: [ClickOutsideDirective, EscapeKeyDirective, E11TitleDirective, E11AppendToDirective, E11ScrollBarDirective, E11StopClickDirective] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11DirectivesModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11DirectivesModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, E11StopClickDirective, ...STANDALONE_DIRECTIVES],
                    exports: [...STANDALONE_DIRECTIVES, E11StopClickDirective],
                }]
        }] });

class E11SanitizePipe {
    constructor(sanitizer) {
        this.sanitizer = sanitizer;
    }
    /**
     * Transforms the given value into a safe version based on the specified type.
     * This method uses Angular's DomSanitizer to bypass the default security mechanisms
     * for different types of content, marking them as safe for binding to the DOM.
     *
     * @param value - The content to be sanitized.
     * @param type - default: html. The type of the content. It can be one of the following:
     *               'html', 'style', 'script', 'url', or 'resourceUrl'.
     *
     * @returns The sanitized content marked as safe for the specified type.
     *
     * @throws Error if an invalid type is specified.
     *
     * Example Usage:
     *
     * 1. Safe HTML:
     *    <div [innerHTML]="htmlContent | sanitize:'html'"></div>
     *    - htmlContent: '<div><strong>Safe HTML content</strong></div>'
     *    - This binds the raw HTML content to the innerHTML property of the div, allowing the HTML to be rendered.
     *
     * 2. Safe Style:
     *    <div [style]="styleContent | sanitize:'style'">Styled content</div>
     *    - styleContent: 'color: red; font-size: 20px;'
     *    - This binds the raw CSS styles to the style attribute of the div, applying the styles to the content.
     *
     * 3. Safe Script:
     *    <script [innerHTML]="scriptContent | sanitize:'script'"></script>
     *    - scriptContent: 'console.log("Safe Script")'
     *    - This binds the raw JavaScript content to the innerHTML property of the script tag, allowing the script to be executed.
     *
     * 4. Safe URL:
     *    <a [href]="urlContent | sanitize:'url'">Safe Link</a>
     *    - urlContent: 'https://example.com'
     *    - This binds the raw URL content to the href attribute of the anchor tag, creating a safe link.
     *
     * 5. Safe Resource URL:
     *    <iframe [src]="resourceUrlContent | sanitize:'resourceUrl'"></iframe>
     *    - resourceUrlContent: 'https://example.com/safe-content'
     *    - This binds the raw resource URL content to the src attribute of the iframe, embedding the resource safely.
     *
     * Example in Template:
     * <div [innerHTML]="someHtmlContent | sanitize:'html'"></div>
     * <div [style]="someStyleContent | sanitize:'style'">Styled content</div>
     * <script [innerHTML]="someScriptContent | sanitize:'script'"></script>
     * <a [href]="someUrlContent | sanitize:'url'">Safe Link</a>
     * <iframe [src]="someResourceUrlContent | sanitize:'resourceUrl'"></iframe>
     *
     */
    transform(value, type = 'html') {
        const sanitizedValue = this.getSanitizedValue(this.sanitizer.sanitize(SecurityContext.HTML, value), type);
        if (sanitizedValue === undefined) {
            throw new Error(`Invalid safe type specified: ${type}`);
        }
        return sanitizedValue;
    }
    getSanitizedValue(value, type = 'html') {
        switch (type) {
            case 'html':
                return this.sanitizer.bypassSecurityTrustHtml(value);
            case 'style':
                return this.sanitizer.bypassSecurityTrustStyle(value);
            case 'script':
                return this.sanitizer.bypassSecurityTrustScript(value);
            case 'url':
                return this.sanitizer.bypassSecurityTrustUrl(value);
            case 'resourceUrl':
                return this.sanitizer.bypassSecurityTrustResourceUrl(value);
            default:
                return undefined;
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11SanitizePipe, deps: [{ token: i1.DomSanitizer }], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: E11SanitizePipe, isStandalone: true, name: "sanitize" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11SanitizePipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'sanitize',
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: i1.DomSanitizer }] });

class E11SummernoteViewDirective {
    set content(value) {
        if (this.summernoteViewDirective) {
            this.summernoteViewDirective.ngxSummernoteView = this._sanitizer.sanitize(SecurityContext.HTML, value);
        }
    }
    constructor(el, renderer) {
        this.el = el;
        this.renderer = renderer;
        this._sanitizer = inject(DomSanitizer);
        this.summernoteViewDirective = new NgxSummernoteViewDirective(this.renderer, this.el);
    }
    ngAfterViewInit() {
        if (this.summernoteViewDirective) {
            this.summernoteViewDirective.ngAfterViewInit();
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11SummernoteViewDirective, deps: [{ token: i0.ElementRef }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.13", type: E11SummernoteViewDirective, isStandalone: true, selector: "[e11SummernoteView]", inputs: { content: ["e11SummernoteView", "content"] }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11SummernoteViewDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[e11SummernoteView]',
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: i0.Renderer2 }], propDecorators: { content: [{
                type: Input,
                args: ['e11SummernoteView']
            }] } });

/**
 * Generated bundle index. Do not edit.
 */

export { ClickOutsideDirective, E11AppendToDirective, E11DirectivesModule, E11SanitizePipe, E11ScrollBarDirective, E11StopClickDirective, E11SummernoteViewDirective, E11TitleDirective, EscapeKeyDirective, ScrollBarAxis, ScrollBarPosition };
